import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase';

// Types for raw track data
interface RawTrackData {
    artist_id: string;
    top_track: string;
    track_genres: string;
    spotify_url: string;
    editorial_playlists: string;
    user_playlists: string;
    mood: string;
    activity: string;
    spotify_plays: string;
    editorial_playlists_info: string;
    independent_playlists_info: string;
    isrc: string;
    created_at: string;
    updated_at: string;
    cm_track: string;
    is_playable: boolean;
    audio_failed: boolean;
}

// Clean, typed interface for tracks
export interface ArtistTrack {
    artist_id: string;
    track_name: string;
    genres: string[];
    spotify_url: string;
    editorial_playlists: number;
    user_playlists: number;
    total_playlists: number;
    mood: string;
    activity: string | null;
    spotify_plays: number;
    isrc: string;
    cm_track_id: string;
    is_playable: boolean;
    audio_failed: boolean;
    created_at: Date;
    updated_at: Date;
}

// Aggregated sonic profile from all tracks
export interface SonicProfile {
    dominant_moods: { mood: string; count: number }[];
    dominant_genres: { genre: string; count: number }[];
    dominant_activities: { activity: string; count: number }[];
    total_tracks: number;
    total_plays: number;
    total_playlists: number;
    editorial_playlist_ratio: number;
}

// Safe number conversion
const safeParseNumber = (value: string | null | undefined): number => {
    if (!value || value === '' || value === 'null' || value === 'undefined') return 0;
    const num = parseFloat(value.replace(/,/g, ''));
    return isNaN(num) ? 0 : num;
};

// Parse genres string into array
const parseGenres = (genresString: string): string[] => {
    if (!genresString || genresString === 'null') return [];
    return genresString.split(',').map(g => g.trim()).filter(g => g.length > 0);
};

// Transform raw track data
const transformTrackData = (raw: RawTrackData): ArtistTrack => {
    const editorial_playlists = safeParseNumber(raw.editorial_playlists);
    const user_playlists = safeParseNumber(raw.user_playlists);
    
    return {
        artist_id: raw.artist_id,
        track_name: raw.top_track || 'Unknown Track',
        genres: parseGenres(raw.track_genres),
        spotify_url: raw.spotify_url || '',
        editorial_playlists,
        user_playlists,
        total_playlists: editorial_playlists + user_playlists,
        mood: raw.mood || '',
        activity: raw.activity && raw.activity !== 'null' ? raw.activity : null,
        spotify_plays: safeParseNumber(raw.spotify_plays),
        isrc: raw.isrc || '',
        cm_track_id: raw.cm_track || '',
        is_playable: raw.is_playable || false,
        audio_failed: raw.audio_failed || false,
        created_at: new Date(raw.created_at),
        updated_at: new Date(raw.updated_at),
    };
};

// Calculate sonic profile from tracks
const calculateSonicProfile = (tracks: ArtistTrack[]): SonicProfile => {
    const moodCounts: { [key: string]: number } = {};
    const genreCounts: { [key: string]: number } = {};
    const activityCounts: { [key: string]: number } = {};
    
    let totalPlays = 0;
    let totalPlaylists = 0;
    let totalEditorialPlaylists = 0;
    
    tracks.forEach(track => {
        // Count moods
        if (track.mood) {
            moodCounts[track.mood] = (moodCounts[track.mood] || 0) + 1;
        }
        
        // Count genres
        track.genres.forEach(genre => {
            genreCounts[genre] = (genreCounts[genre] || 0) + 1;
        });
        
        // Count activities
        if (track.activity) {
            activityCounts[track.activity] = (activityCounts[track.activity] || 0) + 1;
        }
        
        // Sum totals
        totalPlays += track.spotify_plays;
        totalPlaylists += track.total_playlists;
        totalEditorialPlaylists += track.editorial_playlists;
    });
    
    return {
        dominant_moods: Object.entries(moodCounts)
            .map(([mood, count]) => ({ mood, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5),
        dominant_genres: Object.entries(genreCounts)
            .map(([genre, count]) => ({ genre, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5),
        dominant_activities: Object.entries(activityCounts)
            .map(([activity, count]) => ({ activity, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5),
        total_tracks: tracks.length,
        total_plays: totalPlays,
        total_playlists: totalPlaylists,
        editorial_playlist_ratio: totalPlaylists > 0 ? totalEditorialPlaylists / totalPlaylists : 0,
    };
};

export const useArtistTracks = (artistId: string | null | undefined, limit: number = 10) => {
    const [tracks, setTracks] = useState<ArtistTrack[]>([]);
    const [sonicProfile, setSonicProfile] = useState<SonicProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId) {
            setTracks([]);
            setSonicProfile(null);
            return;
        }

        const fetchArtistTracks = async () => {
            setLoading(true);
            setError(null);

            try {
                const { data, error: tracksError } = await supabase
                    .from('tracks')
                    .select('*')
                    .eq('artist_id', artistId)
                    .eq('is_playable', true)
                    .order('spotify_plays', { ascending: false })
                    .limit(limit);

                if (tracksError) throw tracksError;

                const transformedTracks = (data || []).map(transformTrackData);
                setTracks(transformedTracks);
                
                // Calculate sonic profile from all tracks
                if (transformedTracks.length > 0) {
                    setSonicProfile(calculateSonicProfile(transformedTracks));
                } else {
                    setSonicProfile(null);
                }

            } catch (err) {
                console.error('Error fetching artist tracks:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch artist tracks');
                setTracks([]);
                setSonicProfile(null);
            } finally {
                setLoading(false);
            }
        };

        fetchArtistTracks();
    }, [artistId, limit]);

    // Get top track (highest plays)
    const topTrack = tracks.length > 0 ? tracks[0] : null;

    return { 
        tracks, 
        topTrack, 
        sonicProfile, 
        loading, 
        error 
    };
};